/* eslint-disable max-len */
/* eslint-disable no-nested-ternary */

import React, { useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { EVENTS, trackEvent } from '@app/segment';
import { Box } from '@stockx/ui';
import useLocalization from '@app/i18n/hooks/useLocalization';
import { ChevronRightIcon } from '@stockx/icons';
import {
  SwitcherLink,
  ButtonLink,
  PageSwitcherTypes,
  BrowseSwitcherLink,
} from '@app/router';
import { EXPERIMENTS } from '@app/config';
import { useExperiment } from '@app/experiments';
import useMobileNavOpen from '@app/shell/SiteHeader/shared/hooks/useMobileNavOpen';
import {
  SectionContainer,
  Section,
  SectionTitleLink,
  BlogATag,
  SectionSubTitleLink,
  SubSectionContainer,
  SubSection,
  SubSectionTitle,
  SubSectionTitleLink,
  Chevron,
} from '@app/shell/SiteHeader/shared/components/MobileMenu/MainMenuSection.styles';
import { SectionItem } from '@app/shell/SiteHeader/shared/types/MobileMenuSection';

export type MenuItemProps = {
  section: SectionItem;
  blogRender: boolean;
  onClick: Function;
};

/**
 * Currently, the News site does not support a ES-US language prefix, so
 * we redirect to the ES-MX news site. If / when a US-Spanish language
 * news site is launched / supported, this can be removed.
 *
 * @returns localized blog URL string
 */
const handleBlogUrls = (outboundUrl: string, urlPrefix: string) => {
  //
  // Localize about
  if (outboundUrl.includes('company')) {
    return `https://stockx.com/about${urlPrefix}company/`;
  }

  //
  // Localize how it works
  if (outboundUrl.includes('how-it-works')) {
    return `https://stockx.com/about${urlPrefix}how-it-works/`;
  }

  //
  // Fix news urls
  if (outboundUrl.includes('news') && urlPrefix === '/es-us/') {
    return `${outboundUrl}/es-mx/`;
  }

  return `${outboundUrl}${urlPrefix}`;
};

const MenuItem = ({ section, onClick, blogRender }: MenuItemProps) => {
  const { t } = useTranslation();
  const { urlPrefix, langCode } = useLocalization();
  const { handleToggle } = useMobileNavOpen();

  const handleSectionClick = useCallback(
    (section) => {
      trackEvent(EVENTS.NAVIGATION_ITEM_CLICKED, {
        label: section.title,
      });

      if (section.submenu) {
        onClick(section);
      }

      if (
        section.subTitleLink ||
        section.urlKey ||
        section.isLink ||
        section.title === 'Trending Deals'
      ) {
        handleToggle();
      }
    },
    [handleToggle, onClick],
  );

  const subSection =
    section.subsections &&
    section.subsections.map((subsection, index) =>
      subsection.isLink ? (
        <SubSectionLink
          key={index}
          subsection={subsection}
          handleSectionClick={handleSectionClick}
        />
      ) : (
        <SubSectionPlain
          blogRender={blogRender}
          key={index}
          subsection={subsection}
          handleSectionClick={handleSectionClick}
        />
      ),
    );

  const sections = section.subsections ? (
    <SectionContainer>
      <Section role="button">
        {t(section.title)}
        <SectionSubTitleLink>
          <SwitcherLink
            as={section.title === 'Browse' ? PageSwitcherTypes.Browse : ''}
            to={`${urlPrefix}${section.subTitleLink}`}
            onClick={() => handleSectionClick(section)}
          >
            {section.subTitle ? t(section.subTitle) : null}
          </SwitcherLink>
        </SectionSubTitleLink>
      </Section>
      <SubSectionContainer>{subSection}</SubSectionContainer>
    </SectionContainer>
  ) : (
    <SectionContainer>
      <SwitcherLink
        as={section.title === 'Browse' ? PageSwitcherTypes.Browse : ''}
        to={
          section.urlKey
            ? typeof section.urlKey === 'string'
              ? `${urlPrefix}${section.urlKey}`
              : `${urlPrefix}${section.urlKey[langCode]}`
            : ''
        }
        onClick={() => handleSectionClick(section)}
        title={t(section.title)}
        className={`localization-title ${
          section.title === 'My Account' && 'my-account'
        }`}
      >
        <Section title={t(section.title)}>
          {section.outBoundUrl ? (
            <BlogATag
              href={handleBlogUrls(section.outBoundUrl, urlPrefix)}
              onClick={() => handleSectionClick(section)}
              title={t(section.title)}
              className="localization-title"
            >
              {t(section.title)}
            </BlogATag>
          ) : (
            <SectionTitleLink>{t(section.title)}</SectionTitleLink>
          )}
          {section.submenu ? (
            <Chevron
              title={t(section.title)}
              className={section.title === 'My Account' ? 'my-account' : ''}
            >
              <ChevronRightIcon verticalAlign="-0.125em" />
            </Chevron>
          ) : null}
        </Section>
      </SwitcherLink>
    </SectionContainer>
  );

  return <>{sections}</>;
};

const SubSectionLink = ({ subsection, handleSectionClick }) => {
  const { t } = useTranslation();
  const { urlPrefix } = useLocalization();

  return (
    <SubSection role="button">
      <BrowseSwitcherLink
        to={`${urlPrefix}${subsection.link}`}
        onClick={() => handleSectionClick(subsection)}
        style={{ width: '100%' }}
      >
        <SubSectionTitle>{t(subsection.title)}</SubSectionTitle>
      </BrowseSwitcherLink>
    </SubSection>
  );
};

const SubSectionPlain = ({ blogRender, subsection, handleSectionClick }) => {
  const { t } = useTranslation();
  const { urlPrefix } = useLocalization();
  const showTrendingDeals =
    useExperiment(EXPERIMENTS.ab_home_trending_deals_web) === 'variant';

  /* Render unique 'Trending Deals' mobile navigation */
  if (subsection.title === 'Trending Deals') {
    if (!showTrendingDeals) {
      return null;
    }

    return (
      <SectionTitleLink onClick={() => handleSectionClick(subsection)}>
        <SwitcherLink
          to={`${urlPrefix}${subsection.link}`}
          as={PageSwitcherTypes.Browse}
        >
          <SubSectionTitle sx={{ display: 'flex' }}>
            {t(subsection.title)}
            <Box
              as="span"
              textTransform="uppercase"
              backgroundColor="purple.700"
              marginLeft={2}
              padding="2px 4px"
              fontSize="14px"
              color="neutral.white"
            >
              {t('New')}
            </Box>
          </SubSectionTitle>
        </SwitcherLink>
      </SectionTitleLink>
    );
  }

  return (
    <SubSection role="button" onClick={() => handleSectionClick(subsection)}>
      {!blogRender ? (
        <>
          <SubSectionTitle>{t(subsection.title)}</SubSectionTitle>
          <Chevron>
            <ChevronRightIcon verticalAlign="-0.125em" />
          </Chevron>
        </>
      ) : (
        <SubSectionTitleLink>
          <ButtonLink to={`${urlPrefix}${subsection.link}`}>
            {t(subsection.title)}
          </ButtonLink>
        </SubSectionTitleLink>
      )}
    </SubSection>
  );
};

export default memo(MenuItem);
